import { useQuery } from "@apollo/client";
import { type ReactNode, useCallback, useEffect } from "react";
import { ArrowRight as ArrowRightIcon, Share as ShareIcon } from "@tamagui/lucide-icons";
import { Body1, Body2, Body3, Button, Circle, ExternalLink, Modal, RailStepper, ResponsiveImage, Subtitle2, Subtitle3, useMedia, XStack, YStack } from "@medbillai/ui";
import { grantedReferralTermsUrl } from "@medbillai/utils";
import { analyticsService, type ReferralInviteTrigger } from "../../lib/analytics";
import { captureUnreportedError } from "../../lib/apollo/error";
import { errorHandling } from "../../lib/apollo/utils";
import { sentryService } from "../../lib/sentry";
import { usePushGetHelp } from "../../lib/usePushGetHelp";
import { ART, CARDS } from "./images";
import { getReferralCodeQuery } from "./queries";
import { useShareReferral } from "./useShareReferral";
const SHARE_STEPS: Step[] = [{
  title: "Share your link"
}, {
  title: "Your friend uploads a bill and agrees to our terms"
}, {
  title: "You both get $20",
  celebrate: true
}];
const WELCOME_STEPS: Step[] = [{
  title: "Sign up"
}, {
  title: "Escalate your case",
  detail: "Upload a bill and agree to our terms"
}, {
  title: "You both get $20",
  celebrate: true
}];
const WELCOME_TITLE = "A friend invited you to Granted.";
type Step = {
  title: string;
  detail?: string;
  celebrate?: true;
};
export type ReferralSheetContent = "share" | "welcome";
export type ReferralSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  content: ReferralSheetContent;
  trigger: ReferralInviteTrigger;
};
export function ReferralSheet({
  open,
  onOpenChange,
  content,
  trigger
}: ReferralSheetProps) {
  useEffect(() => {
    if (open) {
      analyticsService.trackEvent("ReferralInviteSeen", {
        content,
        trigger
      });
    }
  }, [open, content, trigger]);
  const onDismiss = useCallback(() => analyticsService.trackEvent("ReferralInviteDismissed", {
    content,
    trigger
  }), [content, trigger]);
  const onTermsPress = useCallback(() => analyticsService.trackEvent("ReferralTermsTapped", {
    content
  }), [content]);
  const sheetProps = {
    open,
    onOpenChange,
    onDismiss,
    onTermsPress
  };
  return content === "welcome" ? <WelcomeSheet {...sheetProps} data-sentry-element="WelcomeSheet" data-sentry-component="ReferralSheet" data-sentry-source-file="ReferralSheet.tsx" /> : <ShareSheet {...sheetProps} data-sentry-element="ShareSheet" data-sentry-component="ReferralSheet" data-sentry-source-file="ReferralSheet.tsx" />;
}
type SheetProps = Pick<ReferralSheetProps, "open" | "onOpenChange"> & {
  onDismiss: () => void;
  onTermsPress: () => void;
};
function ShareSheet(sheetProps: SheetProps) {
  const {
    open
  } = sheetProps;
  // cache-and-network: a null read while the program was off for this user
  // must not stick for the session once it turns on.
  const {
    data,
    loading,
    error
  } = useQuery(getReferralCodeQuery, {
    context: errorHandling("caller"),
    fetchPolicy: "cache-and-network",
    skip: !open
  });
  const code = data?.me?.referralCode ?? null;
  const share = useShareReferral();
  useEffect(() => {
    if (error) {
      captureUnreportedError(error, {
        tags: {
          feature: "referral-program"
        }
      });
    }
  }, [error]);
  const handleShare = useCallback(() => {
    if (!code) {
      return;
    }
    share(code).catch((error: unknown) => {
      sentryService.captureException(error, {
        tags: {
          feature: "referral-program"
        }
      });
    });
  }, [code, share]);

  // A null code means the program is off for this user server-side: no link
  // to share, so no Share button.
  const unavailable = !loading && !code;
  const fetching = loading && !code;
  return <ReferralCard {...sheetProps} title="Give $20. Get $20." body="Know someone with a medical bill? You can both get a $20 gift card by following the step below." steps={SHARE_STEPS} action={unavailable ? <Body2 color="$textSubtle" testID="referral-sheet.unavailable">
            Your invite link isn't available right now.
          </Body2> : <Button variant="brand" size="large" iconRight={ShareIcon} loading={fetching} onPress={handleShare}>
            Share invite
          </Button>} data-sentry-element="ReferralCard" data-sentry-component="ShareSheet" data-sentry-source-file="ReferralSheet.tsx" />;
}
function WelcomeSheet(sheetProps: SheetProps) {
  const {
    pushGetHelp
  } = usePushGetHelp();
  const startCase = () => {
    sheetProps.onOpenChange(false);
    pushGetHelp({
      caseType: "BILL_ISSUE_V2",
      title: "Get help with a bill",
      source: "referral_welcome"
    });
  };
  return <ReferralCard {...sheetProps} title={WELCOME_TITLE} body="Here's how you both get a $20 gift card." steps={WELCOME_STEPS} action={<Button variant="brand" size="large" iconRight={ArrowRightIcon} onPress={startCase}>
          Start a case
        </Button>} data-sentry-element="ReferralCard" data-sentry-component="WelcomeSheet" data-sentry-source-file="ReferralSheet.tsx" />;
}
type ReferralCardProps = SheetProps & {
  title: string;
  body: string;
  steps: Step[];
  action: ReactNode;
};
function ReferralCard({
  open,
  onOpenChange,
  onDismiss,
  onTermsPress,
  title,
  body,
  steps,
  action
}: ReferralCardProps) {
  const twoPane = !useMedia().sm;
  return <Modal open={open} onOpenChange={onOpenChange} onDismiss={onDismiss} size={twoPane ? "medium" : "small"} surface="surface2" headerProps={{
    title
  }} data-sentry-element="Modal" data-sentry-component="ReferralCard" data-sentry-source-file="ReferralSheet.tsx">
      <XStack gap="$sp3" data-sentry-element="XStack" data-sentry-source-file="ReferralSheet.tsx">
        {twoPane ? <ReferralArt /> : null}
        <YStack width={twoPane ? "45%" : "100%"} gap="$sp4" justifyContent="space-between" data-sentry-element="YStack" data-sentry-source-file="ReferralSheet.tsx">
          {twoPane ? null : <YStack width="$si16" alignSelf="center">
              <ResponsiveImage src={CARDS} alt="" />
            </YStack>}
          <Body1 color="$textDefault" data-sentry-element="Body1" data-sentry-source-file="ReferralSheet.tsx">{body}</Body1>
          <RailStepper steps={steps.map(({
          title,
          detail,
          celebrate
        }, i) => ({
          key: title,
          status: "todo",
          marker: <StepNumber n={i + 1} />,
          firstLine: "subtitle2",
          content: <YStack>
                  <XStack gap="$sp1">
                    <Subtitle2 color="$textDefault">{title}</Subtitle2>
                    {celebrate ? <Subtitle2>🎉</Subtitle2> : null}
                  </XStack>
                  {detail ? <Body2 color="$textSubtle">{detail}</Body2> : null}
                </YStack>
        }))} data-sentry-element="RailStepper" data-sentry-source-file="ReferralSheet.tsx" />
          <YStack gap="$sp1.5" alignItems="center" data-sentry-element="YStack" data-sentry-source-file="ReferralSheet.tsx">
            {action}
            <YStack alignItems="center" data-sentry-element="YStack" data-sentry-source-file="ReferralSheet.tsx">
              <Body3 color="$textSubtle" textAlign="center" data-sentry-element="Body3" data-sentry-source-file="ReferralSheet.tsx">
                Choose from 2,000+ brands
              </Body3>
              <Body3 color="$textSubtle" textAlign="center" data-sentry-element="Body3" data-sentry-source-file="ReferralSheet.tsx">
                US residents only ·{" "}
                <ExternalLink href={grantedReferralTermsUrl()} title="Terms apply" target="_blank" textDecorationLine="underline" onPress={onTermsPress} data-sentry-element="ExternalLink" data-sentry-source-file="ReferralSheet.tsx" />
              </Body3>
            </YStack>
          </YStack>
        </YStack>
      </XStack>
    </Modal>;
}
function ReferralArt() {
  return <YStack flex={1} borderRadius="$br2" overflow="hidden" data-sentry-element="YStack" data-sentry-component="ReferralArt" data-sentry-source-file="ReferralSheet.tsx">
      <ResponsiveImage src={ART} alt="" objectFit="cover" height="100%" data-sentry-element="ResponsiveImage" data-sentry-source-file="ReferralSheet.tsx" />
    </YStack>;
}
function StepNumber({
  n
}: {
  n: number;
}) {
  return <Circle size="$si3" backgroundColor="$surface2" borderWidth="$border2" borderColor="$borderDefault" data-sentry-element="Circle" data-sentry-component="StepNumber" data-sentry-source-file="ReferralSheet.tsx">
      <Subtitle3 color="$textDefault" data-sentry-element="Subtitle3" data-sentry-source-file="ReferralSheet.tsx">{n}</Subtitle3>
    </Circle>;
}